import React, { useMemo, useState } from 'react'
import { ChevronUp, ChevronDown } from 'lucide-react'

interface RecentEvent {
  timestamp: string
  model: string
  tokens: number
  cost: number
}

interface EventsTableProps {
  data?: RecentEvent[]
  isLoading?: boolean
  error?: string | null
  itemsPerPage?: number
}

type SortField = 'timestamp' | 'model' | 'tokens' | 'cost'
type SortDirection = 'asc' | 'desc'

interface SortHeaderProps {
  field: SortField
  label: string
  isActive: boolean
  direction: SortDirection
  onClick: (field: SortField) => void
}

/**
 * SortHeader button component for table headers
 */
const SortHeader: React.FC<SortHeaderProps> = ({ field, label, isActive, direction, onClick }) => {
  return (
    <button
      onClick={() => onClick(field)}
      className="flex items-center gap-2 hover:text-[#FF5433] transition-colors"
      aria-pressed={isActive}
      aria-label={`Sort by ${label}, currently ${isActive ? direction : 'unsorted'}`}
    >
      <span>{label}</span>
      {isActive && (
        <span className="text-[#FF5433]">
          {direction === 'asc' ? (
            <ChevronUp size={16} aria-label="sorted ascending" />
          ) : (
            <ChevronDown size={16} aria-label="sorted descending" />
          )}
        </span>
      )}
    </button>
  )
}

/**
 * EventsTable displays recent events in a sortable table
 * Columns: timestamp, model, tokens, cost
 * Supports pagination with configurable items per page
 */
const EventsTable: React.FC<EventsTableProps> = ({
  data = [],
  isLoading = false,
  error = null,
  itemsPerPage = 20,
}) => {
  const [sortField, setSortField] = useState<SortField>('timestamp')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [currentPage, setCurrentPage] = useState(1)

  // Sort and paginate data
  const paginatedData = useMemo(() => {
    if (!data || data.length === 0) return { items: [], totalPages: 0, totalItems: 0 }

    // Sort data
    const sortedData = [...data].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]

      let comparison = 0
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal)
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    // Paginate
    const totalItems = sortedData.length
    const totalPages = Math.ceil(totalItems / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const items = sortedData.slice(startIndex, startIndex + itemsPerPage)

    return { items, totalPages, totalItems }
  }, [data, sortField, sortDirection, currentPage, itemsPerPage])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
    setCurrentPage(1) // Reset to first page when sorting
  }

  // Loading state
  if (isLoading) {
    return (
      <div
        className="w-full flex items-center justify-center bg-[#F3F3F3] rounded-puzzle p-8"
        role="status"
        aria-label="Loading table"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#FF5433] border-t-transparent mx-auto mb-2" />
          <p className="text-[#666666] text-sm">Loading table...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div
        className="w-full flex items-center justify-center bg-[#FFEBEB] rounded-puzzle border border-[#D63329] p-8"
        role="alert"
      >
        <div className="text-center">
          <p className="text-[#D63329] text-sm font-bold">Error loading table</p>
          <p className="text-[#666666] text-xs mt-1">{error}</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div
        className="w-full flex items-center justify-center bg-[#F3F3F3] rounded-puzzle p-8"
        role="status"
      >
        <p className="text-[#999999] text-sm">No events available</p>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-x-auto rounded-puzzle border border-[#DCDCDC]">
        <table
          className="w-full text-sm"
          role="grid"
          aria-label="Events table"
          aria-rowcount={paginatedData.totalItems}
        >
          <thead className="bg-[#F3F3F3] border-b border-[#DCDCDC]">
            <tr className="hover:bg-[#E8E8E8]">
              <th className="px-4 py-3 text-left font-bold text-[#000000] w-1/3">
                <SortHeader
                  field="timestamp"
                  label="Timestamp"
                  isActive={sortField === 'timestamp'}
                  direction={sortDirection}
                  onClick={handleSort}
                />
              </th>
              <th className="px-4 py-3 text-left font-bold text-[#000000] w-1/4">
                <SortHeader
                  field="model"
                  label="Model"
                  isActive={sortField === 'model'}
                  direction={sortDirection}
                  onClick={handleSort}
                />
              </th>
              <th className="px-4 py-3 text-right font-bold text-[#000000] w-1/4">
                <SortHeader
                  field="tokens"
                  label="Tokens"
                  isActive={sortField === 'tokens'}
                  direction={sortDirection}
                  onClick={handleSort}
                />
              </th>
              <th className="px-4 py-3 text-right font-bold text-[#000000] w-1/6">
                <SortHeader
                  field="cost"
                  label="Cost"
                  isActive={sortField === 'cost'}
                  direction={sortDirection}
                  onClick={handleSort}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.items.map((event, index) => (
              <tr
                key={`${event.timestamp}-${index}`}
                className="border-b border-[#DCDCDC] hover:bg-[#F3F3F3] transition-colors"
                role="row"
              >
                <td className="px-4 py-3 text-[#000000]">
                  {new Date(event.timestamp).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-puzzle text-xs font-bold bg-[#FFF8F7] text-[#FF5433]">
                    {event.model}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-[#000000]">
                  {event.tokens.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right text-[#000000]">
                  ${typeof event.cost === 'number' ? event.cost.toFixed(4) : event.cost}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginatedData.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-[#666666]">
          <span>
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, paginatedData.totalItems)} of{' '}
            {paginatedData.totalItems} events
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-puzzle border border-[#DCDCDC] hover:bg-[#F3F3F3] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: paginatedData.totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-2 py-1 rounded-puzzle text-xs font-bold transition-colors ${
                    currentPage === page
                      ? 'bg-[#FF5433] text-white'
                      : 'border border-[#DCDCDC] hover:bg-[#F3F3F3]'
                  }`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(paginatedData.totalPages, currentPage + 1))}
              disabled={currentPage === paginatedData.totalPages}
              className="px-3 py-1 rounded-puzzle border border-[#DCDCDC] hover:bg-[#F3F3F3] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventsTable
